import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid, Card } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import { getApps } from '../services/api';

const features = [
  { icon: RocketLaunchIcon, title: 'Innovación', desc: 'Tecnologías de vanguardia' },
  { icon: SecurityIcon, title: 'Seguridad', desc: 'Protección empresarial' },
  { icon: SpeedIcon, title: 'Automatización', desc: 'Procesos optimizados' },
  { icon: IntegrationInstructionsIcon, title: 'Integración', desc: 'Sistemas conectados' },
];

const Home = () => {
  const [appCount, setAppCount] = useState(0);

  useEffect(() => {
    getApps().then(apps => setAppCount(apps.length)).catch(() => {});
  }, []);

  return (
    <Box sx={{ minHeight: 'calc(100vh - 48px)', backgroundColor: '#f4f4f4', display: 'flex', flexDirection: 'column' }}>
      {/* Hero */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #161616 0%, #262626 50%, #0f62fe 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background: 'radial-gradient(circle, rgba(15,98,254,0.3) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: 'rgba(15,98,254,0.2)',
                  border: '1px solid rgba(15,98,254,0.4)',
                  borderRadius: '20px',
                  px: 2,
                  py: 0.5,
                  mb: 2,
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#4589ff' }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Área de I+D
                </Typography>
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  lineHeight: 1.2,
                }}
              >
                Catálogo de Apps
                <br />
                <Box component="span" sx={{ color: '#4589ff' }}>Empresariales</Box>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  color: 'rgba(255,255,255,0.7)',
                  maxWidth: 500,
                  lineHeight: 1.6,
                }}
              >
                Soluciones innovadoras desarrolladas por nuestro equipo para optimizar los procesos de tu organización.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  component={Link}
                  to="/catalog"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    backgroundColor: '#0f62fe',
                    borderRadius: '8px',
                    px: 3,
                    '&:hover': { backgroundColor: '#0043ce' },
                  }}
                >
                  Ver Catálogo
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '16px',
                    p: 4,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '4rem',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #4589ff 0%, #0f62fe 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      lineHeight: 1,
                    }}
                  >
                    {appCount}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1 }}>
                    Apps Disponibles
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ py: 6, flexGrow: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.8rem', md: '2.2rem' },
              letterSpacing: '0.02em',
              display: 'inline',
            }}
          >
            <Box
              component="span"
              sx={{
                color: '#161616',
                textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              Nuestros{' '}
            </Box>
            <Box
              component="span"
              sx={{
                color: '#0f62fe',
                textShadow: '2px 2px 8px rgba(15,98,254,0.3)',
              }}
            >
              Objetivos
            </Box>
          </Typography>
        </Box>
        <Grid container spacing={2} justifyContent="center">
          {features.map(({ icon: Icon, title, desc }, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                sx={{
                  p: 2.5,
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#0f62fe',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    backgroundColor: '#f4f4f4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                  }}
                >
                  <Icon sx={{ fontSize: 20, color: '#0f62fe' }} />
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          py: 2,
          backgroundColor: '#161616',
          mt: 'auto',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
              IBM I+D - Área de Innovación
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>
              © {new Date().getFullYear()} Todos los derechos reservados
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
